import styled from 'styled-components'

export const PartsContainer = styled.div`
  padding: ${({ theme }) => theme.spacing[60]};
`

export const PartsTitle = styled.h2`
  margin: 0 0 ${({ theme }) => theme.spacing[60]} 0;
  color: ${({ theme }) => theme.palette.text.default};
  font-size: ${({ theme }) => theme.typography.heading.size.lg};
  font-weight: ${({ theme }) => theme.typography.weight.semibold};
`

export const SectionTitle = styled.h3`
  margin: ${({ theme }) => theme.spacing[60]} 0 ${({ theme }) => theme.spacing[40]} 0;
  color: ${({ theme }) => theme.palette.text.default};
  font-size: ${({ theme }) => theme.typography.heading.size.md};
  font-weight: ${({ theme }) => theme.typography.weight.semibold};
`

export const PartsTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  background: ${({ theme }) => theme.palette.background.default};
  border-radius: ${({ theme }) => theme.border.radius.md};
  overflow: hidden;
  box-shadow: ${({ theme }) => theme.elevation.bottom.sm};
`

export const TableHeader = styled.th`
  background: ${({ theme }) => theme.palette.background.alternate};
  border: ${({ theme }) => theme.border.width.sm} solid ${({ theme }) => theme.palette.border.default};
  padding: ${({ theme }) => theme.spacing[30]};
  text-align: left;
  font-weight: ${({ theme }) => theme.typography.weight.semibold};
  color: ${({ theme }) => theme.palette.text.default};
`

export const TableCell = styled.td`
  border: ${({ theme }) => theme.border.width.sm} solid ${({ theme }) => theme.palette.border.default};
  padding: ${({ theme }) => theme.spacing[30]};
  color: ${({ theme }) => theme.palette.text.default};
`

export const PartDescription = styled.div`
  font-size: ${({ theme }) => theme.typography.body.size.sm};
  color: ${({ theme }) => theme.palette.text.support};
  margin-top: ${({ theme }) => theme.spacing[10]};
`

export const PartTypeBadge = styled.span`
  display: inline-block;
  padding: ${({ theme }) => `${theme.spacing[10]} ${theme.spacing[20]}`};
  border-radius: ${({ theme }) => theme.border.radius.sm};
  font-size: ${({ theme }) => theme.typography.body.size.xs};
  font-weight: ${({ theme }) => theme.typography.weight.medium};
  background: ${({ theme }) => theme.palette.background.emphasis};
  color: ${({ theme }) => theme.palette.primary.default};
`

export const ModelsContainer = styled.div`
  font-size: ${({ theme }) => theme.typography.body.size.sm};
`

export const ModelTag = styled.div<{ $discontinued?: boolean }>`
  display: inline-block;
  background-color: ${({ theme, $discontinued }) => 
    $discontinued ? theme.palette.background.critical : theme.palette.background.success};
  color: ${({ theme, $discontinued }) => 
    $discontinued ? theme.palette.status.critical : theme.palette.success.default};
  padding: ${({ theme }) => `${theme.spacing[10]} ${theme.spacing[20]}`};
  margin: ${({ theme }) => theme.spacing[10]};
  border-radius: ${({ theme }) => theme.border.radius.sm};
  font-size: ${({ theme }) => theme.typography.body.size.xs};
  font-weight: ${({ theme }) => theme.typography.weight.medium};
`

export const UnknownModelTag = styled.div`
  display: inline-block;
  background-color: ${({ theme }) => theme.palette.background.critical};
  color: ${({ theme }) => theme.palette.status.critical};
  padding: ${({ theme }) => `${theme.spacing[10]} ${theme.spacing[20]}`};
  margin: ${({ theme }) => theme.spacing[10]};
  border-radius: ${({ theme }) => theme.border.radius.sm};
  font-size: ${({ theme }) => theme.typography.body.size.xs};
  font-weight: ${({ theme }) => theme.typography.weight.medium};
`

export const NoModelsText = styled.span`
  color: ${({ theme }) => theme.palette.text.support};
  font-style: italic;
  font-size: ${({ theme }) => theme.typography.body.size.sm};
`

export const BooleanIndicator = styled.span<{ $value: boolean }>`
  color: ${({ theme, $value }) => 
    $value ? theme.palette.success.default : theme.palette.status.critical};
  font-weight: ${({ theme }) => theme.typography.weight.medium};
`

export const ActionButton = styled.button<{ $variant?: 'secondary' | 'danger' }>`
  padding: ${({ theme }) => `${theme.spacing[20]} ${theme.spacing[30]}`};
  margin-right: ${({ theme }) => theme.spacing[20]};
  border: ${({ theme }) => theme.border.width.sm} solid;
  border-radius: ${({ theme }) => theme.border.radius.md};
  font-size: ${({ theme }) => theme.typography.body.size.sm};
  font-weight: ${({ theme }) => theme.typography.weight.medium};
  cursor: pointer;
  transition: all 0.2s ease-in-out;

  ${({ theme, $variant = 'secondary' }) => {
    switch ($variant) {
      case 'danger':
        return `
          background: ${theme.palette.status.critical};
          color: ${theme.palette.text.alternate};
          border-color: ${theme.palette.status.critical};
          
          &:hover {
            background: ${theme.palette.danger.active};
            border-color: ${theme.palette.danger.active};
          }
        `
      case 'secondary':
      default:
        return `
          background: transparent;
          color: ${theme.palette.primary.default};
          border-color: ${theme.palette.primary.default};
          
          &:hover {
            background: ${theme.palette.background.emphasis};
          }
        `
    }
  }}

  &:focus {
    outline: ${({ theme }) => theme.component.interaction.focus.outline};
    outline-offset: ${({ theme }) => theme.component.interaction.focus.outlineOffset};
  }
`

export const HeaderContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${({ theme }) => theme.spacing[80]};
`

export const LoadingContainer = styled.div`
  text-align: center;
  padding: ${({ theme }) => theme.spacing[80]};
`

export const ErrorContainer = styled.div`
  background: ${({ theme }) => theme.palette.background.critical};
  color: ${({ theme }) => theme.palette.status.critical};
  padding: ${({ theme }) => theme.spacing[40]};
  border-radius: ${({ theme }) => theme.border.radius.md};
  margin-bottom: ${({ theme }) => theme.spacing[60]};
`

export const FormSection = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing[80]};
`

export const FormGrid = styled.div<{ $columns?: number }>`
  display: grid;
  grid-template-columns: ${({ $columns = 2 }) => `repeat(${$columns}, 1fr)`};
  gap: ${({ theme }) => theme.spacing[40]};
`

export const FormActions = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing[40]};
`

export const ModelsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: ${({ theme }) => theme.spacing[20]};
  margin-top: ${({ theme }) => theme.spacing[20]};
`

export const ModelLabel = styled.label`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[20]};
  padding: ${({ theme }) => theme.spacing[20]};
  border: ${({ theme }) => theme.border.width.sm} solid ${({ theme }) => theme.palette.border.default};
  border-radius: ${({ theme }) => theme.border.radius.sm};
  font-size: ${({ theme }) => theme.typography.body.size.sm};
  cursor: pointer;
  transition: all 0.2s ease-in-out;

  &:hover {
    background-color: ${({ theme }) => theme.palette.background.emphasis};
    border-color: ${({ theme }) => theme.palette.primary.default};
  }
`

export const ModelInfo = styled.small`
  color: ${({ theme }) => theme.palette.text.support};
  margin-left: auto;
`

export const DiscontinuedText = styled.span`
  color: ${({ theme }) => theme.palette.status.critical};
`

export const NoModelsMessage = styled.p`
  color: ${({ theme }) => theme.palette.text.support};
  font-style: italic;
  margin: ${({ theme }) => theme.spacing[20]} 0;
`